import React, { useState, useEffect } from 'react';
import { ref, onValue, get, push, set, serverTimestamp } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../firebase';

function PurchaseReturn({ onClose, billToEdit }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    type: "Purchase Return",
    supplier: "",
    address: "",
    city: "",
    pinCode: "",
    returnNo: "1",
    returnDate: new Date().toISOString().split('T')[0],
    invoiceNo: "",
    invoiceDate: "",
  });
  const [items, setItems] = useState([
    {
      id: 1,
      barCode: "",
      itemDescription: "",
      uom: "",
      batch: "",
      expiryDate: "",
      qty: "",
      avgAvailableQty: "",
      returnQty: "",
      disc: "",
      camt: "",
      samt: "",
      cessAmt: "",
      mrp: "",
      remarks: "",
      value: "",
      valueWithTax: ""
    }
  ]);
  const [summary, setSummary] = useState({
    grossAmount: 0,
    discount: 0,
    cgstTax: 0,
    sgstTax: 0,
    igstTax: 0,
    cessTax: 0,
    add: 0,
    less: 0,
    cashDiscount: 0,
    roundedOff: 0,
    netAmount: 0
  });
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  const generateUniqueBarcode = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    return `BAR${timestamp}${randomNum}`;
  };

  useEffect(() => {
    if (billToEdit) {
      setFormData({
        type: billToEdit.formData?.type || "Purchase Return",
        supplier: billToEdit.formData?.supplier || "",
        address: billToEdit.formData?.address || "",
        city: billToEdit.formData?.city || "",
        pinCode: billToEdit.formData?.pinCode || "",
        returnNo: billToEdit.formData?.returnNo || "1",
        returnDate: billToEdit.formData?.returnDate || "",
        invoiceNo: billToEdit.formData?.invoiceNo || "",
        invoiceDate: billToEdit.formData?.invoiceDate || "",
      });
      
      setItems(
        billToEdit.items && billToEdit.items.length > 0
          ? billToEdit.items.map((item, index) => ({
              id: item.id || index + 1,
              barCode: item.barCode || "",
              itemDescription: item.itemDescription || "",
              uom: item.uom || "",
              batch: item.batch || "",
              expiryDate: item.expiryDate || "",
              qty: item.qty?.toString() || "",
              avgAvailableQty: item.avgAvailableQty?.toString() || "",
              returnQty: item.returnQty?.toString() || "",
              disc: item.disc?.toString() || "",
              camt: item.camt?.toString() || "",
              samt: item.samt?.toString() || "",
              cessAmt: item.cessAmt?.toString() || "",
              mrp: item.mrp?.toString() || "",
              remarks: item.remarks || "",
              value: item.value?.toString() || "",
              valueWithTax: item.valueWithTax?.toString() || ""
            }))
          : [
              {
                id: 1,
                barCode: "",
                itemDescription: "",
                uom: "",
                batch: "",
                expiryDate: "",
                qty: "",
                avgAvailableQty: "",
                returnQty: "",
                disc: "",
                camt: "",
                samt: "",
                cessAmt: "",
                mrp: "",
                remarks: "",
                value: "",
                valueWithTax: ""
              }
            ]
      );
      
      setSummary({
        grossAmount: parseFloat(billToEdit.summary?.grossAmount) || 0,
        discount: parseFloat(billToEdit.summary?.discount) || 0,
        cgstTax: parseFloat(billToEdit.summary?.cgstTax) || 0,
        sgstTax: parseFloat(billToEdit.summary?.sgstTax) || 0,
        igstTax: parseFloat(billToEdit.summary?.igstTax) || 0,
        cessTax: parseFloat(billToEdit.summary?.cessTax) || 0,
        add: parseFloat(billToEdit.summary?.add) || 0,
        less: parseFloat(billToEdit.summary?.less) || 0,
        cashDiscount: parseFloat(billToEdit.summary?.cashDiscount) || 0,
        roundedOff: parseFloat(billToEdit.summary?.roundedOff) || 0,
        netAmount: parseFloat(billToEdit.summary?.netAmount) || 0
      });
      
      setSelectedProductIds(
        billToEdit.items && billToEdit.items.length > 0 
          ? billToEdit.items.map(item => item.barCode || "").filter(barCode => barCode !== "")
          : []
      );
    }
  }, [billToEdit]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const supplierList = Object.keys(data).map((key) => ({
          id: key,
          name: data[key].supplierName,
          address: data[key].address || '',
          city: data[key].city || '',
          pinCode: data[key].zipCode || '',
        }));
        setSuppliers(supplierList);
      } else {
        setSuppliers([]);
      }
    });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const invoicesRef = ref(database, `users/${encodedEmail}/invoices`);
    onValue(invoicesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invoiceList = Object.keys(data).map((key) => ({
          id: key,
          invoiceNo: data[key].supplierDetails.invoiceNo,
          invoiceDate: data[key].supplierDetails.invoiceDate,
          supplier: data[key].supplierDetails.supplier,
          items: data[key].items.map(item => ({
            ...item,
            barCode: item.barcode || item.barCode || `BAR${item.id}_${key}`
          })) || []
        }));
        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }
    });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || billToEdit) return;
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const returnsRef = ref(database, `users/${encodedEmail}/purchaseReturns`);
    get(returnsRef)
      .then((snapshot) => {
        let maxReturnNo = 0;
        if (snapshot.exists()) {
          const returns = snapshot.val();
          const returnNos = Object.values(returns)
            .map((ret) => parseInt(ret.formData?.returnNo, 10))
            .filter((num) => !isNaN(num));
          maxReturnNo = returnNos.length > 0 ? Math.max(...returnNos) : 0;
        }
        setFormData((prev) => ({ ...prev, returnNo: (maxReturnNo + 1).toString() }));
      })
      .catch((error) => {
        console.error('Error fetching Return No:', error);
      });
  }, [currentUser, billToEdit]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('input[name="itemDescription"]') && !e.target.closest('.suggestions-table')) {
        setFilteredItems([]);
        setActiveRowId(null);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (billToEdit) {
      return;
    }
    
    if (formData.invoiceNo && formData.supplier) {
      const selectedInvoice = invoices.find(
        (inv) => inv.invoiceNo === formData.invoiceNo && inv.supplier === formData.supplier
      );
      if (selectedInvoice && selectedInvoice.items) {
        const availableItems = selectedInvoice.items.filter(
          item => !selectedProductIds.includes(item.barCode)
        );
        setInvoiceItems(availableItems);
        calculateSummary(availableItems);
      } else {
        setInvoiceItems([]);
        resetItems();
      }
    } else {
      setInvoiceItems([]);
      resetItems();
    }
  }, [formData.invoiceNo, formData.supplier, invoices, selectedProductIds, billToEdit]);

  const resetItems = () => {
    setItems([
      {
        id: 1,
        barCode: "",
        itemDescription: "",
        uom: "",
        batch: "",
        expiryDate: "",
        qty: "",
        avgAvailableQty: "",
        returnQty: "",
        disc: "",
        camt: "",
        samt: "",
        cessAmt: "",
        mrp: "",
        remarks: "",
        value: "",
        valueWithTax: ""
      }
    ]);
    setSummary({
      grossAmount: 0,
      discount: 0,
      cgstTax: 0,
      sgstTax: 0,
      igstTax: 0,
      cessTax: 0,
      add: 0,
      less: 0,
      cashDiscount: 0,
      roundedOff: 0,
      netAmount: 0
    });
    setSelectedProductIds([]);
  };

  const calculateSummary = (itemsToCalculate = items) => {
    const grossAmount = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const value = parseFloat(item.value) || 0;
        return sum + (value * returnQty);
      }, 0)
      .toFixed(2);
    const discount = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const discPercent = parseFloat(item.disc) || 0;
        const value = parseFloat(item.value) || 0;
        return sum + ((value * discPercent / 100) * returnQty);
      }, 0)
      .toFixed(2);
    const cgstTax = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const cgstPercent = parseFloat(item.camt) || 0;
        const value = parseFloat(item.value) || 0;
        const discountAmount = ((value * (parseFloat(item.disc) || 0) / 100));
        const taxableAmount = (value - discountAmount) * returnQty;
        return sum + (taxableAmount * cgstPercent / 100);
      }, 0)
      .toFixed(2);
    const sgstTax = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const sgstPercent = parseFloat(item.samt) || 0;
        const value = parseFloat(item.value) || 0;
        const discountAmount = ((value * (parseFloat(item.disc) || 0) / 100));
        const taxableAmount = (value - discountAmount) * returnQty;
        return sum + (taxableAmount * sgstPercent / 100);
      }, 0)
      .toFixed(2);
    const igstTax = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const igstPercent = parseFloat(item.igstAmt) || 0;
        const value = parseFloat(item.value) || 0;
        const discountAmount = ((value * (parseFloat(item.disc) || 0) / 100));
        const taxableAmount = (value - discountAmount) * returnQty;
        return sum + (taxableAmount * igstPercent / 100);
      }, 0)
      .toFixed(2);
    const cessTax = itemsToCalculate
      .reduce((sum, item) => {
        const returnQty = parseFloat(item.returnQty) || 0;
        const cessAmt = parseFloat(item.cessAmt) || 0;
        return sum + (cessAmt * returnQty);
      }, 0)
      .toFixed(2);
    const netAmount = (
      parseFloat(grossAmount) -
      parseFloat(discount) +
      parseFloat(cgstTax) +
      parseFloat(sgstTax) +
      parseFloat(igstTax) +
      parseFloat(cessTax) +
      parseFloat(summary.add || 0) -
      parseFloat(summary.less || 0) -
      parseFloat(summary.cashDiscount || 0) +
      parseFloat(summary.roundedOff || 0)
    ).toFixed(2);
    setSummary({
      grossAmount,
      discount,
      cgstTax,
      sgstTax,
      igstTax,
      cessTax,
      add: summary.add,
      less: summary.less,
      cashDiscount: summary.cashDiscount,
      roundedOff: summary.roundedOff,
      netAmount
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };
      if (name === 'supplier') {
        const selectedSupplier = suppliers.find((s) => s.name === value);
        if (selectedSupplier) {
          updatedFormData.address = selectedSupplier.address;
          updatedFormData.city = selectedSupplier.city;
          updatedFormData.pinCode = selectedSupplier.pinCode;
        } else {
          updatedFormData.address = '';
          updatedFormData.city = '';
          updatedFormData.pinCode = '';
        }
        updatedFormData.invoiceNo = '';
        updatedFormData.invoiceDate = '';
        resetItems();
      }
      if (name === 'invoiceNo') {
        const selectedInvoice = invoices.find((inv) => inv.invoiceNo === value && inv.supplier === formData.supplier);
        if (selectedInvoice) {
          updatedFormData.invoiceDate = selectedInvoice.invoiceDate;
        } else {
          updatedFormData.invoiceDate = '';
        }
        resetItems();
      }
      return updatedFormData;
    });
  };

  const handleItemChange = (index, field, value) => {
    const numericFields = [
      "barCode", "qty", "avgAvailableQty", "returnQty", "disc",
      "camt", "samt", "cessAmt", "mrp", "value", "valueWithTax"
    ];
    const textOnlyFields = ["uom"];

    if (numericFields.includes(field) && value && !/^\d*\.?\d*$/.test(value)) {
      alert(field.replace(/([A-Z])/g, ' $1').trim() + " must contain only numbers.");
      return;
    }
    if (textOnlyFields.includes(field) && value && !/^[a-zA-Z]*$/.test(value)) {
      alert("UOM must contain only letters.");
      return;
    }

    setItems(prevItems => {
      const updatedItems = prevItems.map((item, idx) => {
        if (idx === index) {
          const updatedItem = { ...item, [field]: value };

          if (field === "returnQty") {
            const returnQty = parseFloat(value) || 0;
            const availableQty = parseFloat(item.avgAvailableQty) || 0;
            if (returnQty > availableQty) {
              updatedItem.returnQty = availableQty.toString();
              alert(`Return quantity cannot exceed available quantity (${availableQty}).`);
              return updatedItem;
            }
          }

          if (field === "itemDescription" && formData.invoiceNo) {
            const query = value.toLowerCase();
            if (query.length > 0) {
              const filtered = invoiceItems.filter(item =>
                item.item && item.item.toLowerCase().includes(query)
              );
              setFilteredItems(filtered.slice(0, 5));
              setActiveRowId(index);
              setActiveSuggestionIndex(-1);
            } else {
              setFilteredItems([]);
              setActiveRowId(null);
              setActiveSuggestionIndex(-1);
            }
          } else if (field === "itemDescription") {
            setFilteredItems([]);
            setActiveRowId(null);
            setActiveSuggestionIndex(-1);
          }

          if (
            ["returnQty", "disc", "camt", "samt", "cessAmt", "value"].includes(field)
          ) {
            const qty = parseFloat(updatedItem.returnQty) || 0;
            const rate = parseFloat(updatedItem.value) || 0;
            const discountPercent = parseFloat(updatedItem.disc) || 0;
            const cgstPercent = parseFloat(updatedItem.camt) || 0;
            const sgstPercent = parseFloat(updatedItem.samt) || 0;
            const cessPercent = parseFloat(updatedItem.cessAmt) || 0;
            const discountAmount = (rate * discountPercent / 100);
            const taxableAmount = (rate - discountAmount) * qty;
            const cgstAmount = taxableAmount * cgstPercent / 100;
            const sgstAmount = taxableAmount * sgstPercent / 100;
            const cessAmount = taxableAmount * cessPercent / 100;
            updatedItem.valueWithTax = (
              taxableAmount +
              cgstAmount +
              sgstAmount +
              cessAmount
            ).toFixed(2);
          }

          return updatedItem;
        }
        return item;
      });

      if (
        ["returnQty", "disc", "camt", "samt", "cessAmt", "value"].includes(field)
      ) {
        calculateSummary(updatedItems);
      }
      return updatedItems;
    });
  };

  const handleItemSelect = (index, selectedItem) => {
    if (selectedProductIds.includes(selectedItem.barCode)) return;
    const uniqueBarcode = selectedItem.barCode || generateUniqueBarcode();
    
    const selectedInvoice = invoices.find(inv => 
      inv.invoiceNo === formData.invoiceNo && inv.supplier === formData.supplier
    );
    const isIgstInvoice = selectedInvoice && selectedInvoice.summary?.igstAmt > 0;
    
    let cgstPercent = selectedItem.cgstPercent || '';
    let sgstPercent = selectedItem.sgstPercent || '';
    
    if (isIgstInvoice && cgstPercent && sgstPercent) {
      const cgstValue = parseFloat(cgstPercent);
      const sgstValue = parseFloat(sgstPercent);
      
      if (cgstValue === sgstValue && cgstValue > 0) {
        const individualRate = cgstValue / 2;
        cgstPercent = individualRate.toString();
        sgstPercent = individualRate.toString();
      }
    }
    
    setItems((prevItems) =>
      prevItems.map((item, idx) =>
        idx === index
          ? {
              ...item,
              barCode: uniqueBarcode,
              itemDescription: selectedItem.item || '',
              uom: selectedItem.unit || '',
              batch: selectedItem.batch || '',
              expiryDate: selectedItem.eDate || '',
              qty: selectedItem.qty || '',
              avgAvailableQty: selectedItem.qty || '',
              returnQty: '',
              mrp: selectedItem.mrp || '',
              disc: selectedItem.discPercent || '',
              camt: cgstPercent,
              samt: sgstPercent,
              cessAmt: selectedItem.cessPercent || '',
              value: selectedItem.pRate || '',
              valueWithTax: selectedItem.netValue || '',
            }
          : item
      )
    );
    setSelectedProductIds(prev => [...prev, uniqueBarcode]);
    setFilteredItems([]);
    setActiveRowId(null);
    setActiveSuggestionIndex(-1);
    calculateSummary();
  };

  const handleKeyDown = (e, index) => {
    if (filteredItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleItemSelect(index, filteredItems[activeSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setFilteredItems([]);
      setActiveRowId(null);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleProductSelect = (selectedItem) => {
    if (selectedProductIds.includes(selectedItem.barCode)) return;
    const emptyRowIndex = items.findIndex(item => !item.itemDescription);
    const targetIndex = emptyRowIndex !== -1 ? emptyRowIndex : items.length;
    const uniqueBarcode = selectedItem.barCode || generateUniqueBarcode();
    
    const selectedInvoice = invoices.find(inv => 
      inv.invoiceNo === formData.invoiceNo && inv.supplier === formData.supplier
    );
    const isIgstInvoice = selectedInvoice && selectedInvoice.summary?.igstAmt > 0;
    
    let cgstPercent = selectedItem.cgstPercent || '';
    let sgstPercent = selectedItem.sgstPercent || '';
    
    if (isIgstInvoice && cgstPercent && sgstPercent) {
      const cgstValue = parseFloat(cgstPercent);
      const sgstValue = parseFloat(sgstPercent);
      
      if (cgstValue === sgstValue && cgstValue > 0) {
        const individualRate = cgstValue / 2;
        cgstPercent = individualRate.toString();
        sgstPercent = individualRate.toString();
      }
    }
    
    if (emptyRowIndex === -1) {
      const newItem = {
        id: items.length + 1,
        barCode: uniqueBarcode,
        itemDescription: selectedItem.item || '',
        uom: selectedItem.unit || '',
        batch: selectedItem.batch || '',
        expiryDate: selectedItem.eDate || '',
        qty: selectedItem.qty || '',
        avgAvailableQty: selectedItem.qty || '',
        returnQty: '',
        mrp: selectedItem.mrp || '',
        disc: selectedItem.discPercent || '',
        camt: cgstPercent,
        samt: sgstPercent,
        cessAmt: selectedItem.cessPercent || '',
        value: selectedItem.pRate || '',
        valueWithTax: selectedItem.netValue || '',
        remarks: ""
      };
      setItems(prevItems => [...prevItems, newItem]);
      setSelectedProductIds(prev => [...prev, uniqueBarcode]);
    } else {
      handleItemSelect(targetIndex, selectedItem);
    }
    calculateSummary();
  };

  const handleDeleteItem = (index, barCode) => {
    setItems(prevItems => {
      const updatedItems = prevItems.filter((_, idx) => idx !== index);
      if (updatedItems.length === 0) {
        return [{
          id: 1,
          barCode: "",
          itemDescription: "",
          uom: "",
          batch: "",
          expiryDate: "",
          qty: "",
          avgAvailableQty: "",
          returnQty: "",
          disc: "",
          camt: "",
          samt: "",
          cessAmt: "",
          mrp: "",
          remarks: "",
          value: "",
          valueWithTax: ""
        }];
      }
      calculateSummary(updatedItems);
      return updatedItems.map((item, idx) => ({ ...item, id: idx + 1 }));
    });
    setSelectedProductIds(prev => prev.filter(id => id !== barCode));
  };

  const handleSummaryChange = (e) => {
    const { name, value } = e.target;
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      alert(`${name.replace(/([A-Z])/g, ' $1').toUpperCase()} must contain only numbers.`);
      return;
    }
    setSummary((prev) => {
      const updatedSummary = { ...prev, [name]: parseFloat(value) || 0 };
      const netAmount = (
        parseFloat(updatedSummary.grossAmount || 0) -
        parseFloat(updatedSummary.discount || 0) +
        parseFloat(updatedSummary.cgstTax || 0) +
        parseFloat(updatedSummary.sgstTax || 0) +
        parseFloat(updatedSummary.igstTax || 0) +
        parseFloat(updatedSummary.cessTax || 0) +
        parseFloat(updatedSummary.add || 0) -
        parseFloat(updatedSummary.less || 0) -
        parseFloat(updatedSummary.cashDiscount || 0) +
        parseFloat(updatedSummary.roundedOff || 0)
      ).toFixed(2);
      return { ...updatedSummary, netAmount };
    });
  };

  const handleAddItem = () => {
    const newItem = {
      id: items.length + 1,
      barCode: "",
      itemDescription: "",
      uom: "",
      batch: "",
      expiryDate: "",
      qty: "",
      avgAvailableQty: "",
      returnQty: "",
      disc: "",
      camt: "",
      samt: "",
      cessAmt: "",
      mrp: "",
      remarks: "",
      value: "",
      valueWithTax: ""
    };
    setItems((prevItems) => [...prevItems, newItem]);
  };

  const saveReturn = async () => {
    if (isSaving) return;
    if (!currentUser) {
      alert('Please login to save purchase return');
      return;
    }
    if (!formData.invoiceNo || !formData.supplier) {
      alert('Supplier and Invoice number are required');
      return;
    }
    if (items.every((item) => !item.itemDescription)) {
      alert('At least one item with description is required');
      return;
    }
    if (items.some((item) => item.returnQty && parseFloat(item.returnQty) > parseFloat(item.avgAvailableQty))) {
      alert('Return quantity cannot exceed available quantity for one or more items.');
      return;
    }
    setIsSaving(true);
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const returnsRef = ref(database, `users/${encodedEmail}/purchaseReturns`);
      const snapshot = await get(returnsRef);
      let isDuplicateReturnNo = false;
      let isInvoiceAlreadyReturned = false;
      if (snapshot.exists()) {
        const returns = snapshot.val();
        Object.entries(returns).forEach(([key, ret]) => {
          if (
            ret.formData &&
            ret.formData.returnNo === formData.returnNo &&
            (!billToEdit || (billToEdit && key !== billToEdit.id))
          ) {
            isDuplicateReturnNo = true;
          }
          if (
            ret.formData &&
            ret.formData.invoiceNo === formData.invoiceNo &&
            (!billToEdit || (billToEdit && key !== billToEdit.id))
          ) {
            isInvoiceAlreadyReturned = true;
          }
        });
      }
      if (isDuplicateReturnNo) {
        alert('Return number already exists. Please try again.');
        return;
      }
      if (isInvoiceAlreadyReturned) {
        alert('This invoice has already been returned. An invoice cannot be returned more than once.');
        return;
      }
      const returnData = {
        userEmail: currentUser.email,
        formData,
        items: items.map(item => ({
          ...item,
          qty: parseFloat(item.qty) || 0,
          avgAvailableQty: parseFloat(item.avgAvailableQty) || 0,
          returnQty: parseFloat(item.returnQty) || 0,
          disc: parseFloat(item.disc) || 0,
          camt: parseFloat(item.camt) || 0,
          samt: parseFloat(item.samt) || 0,
          cessAmt: parseFloat(item.cessAmt) || 0,
          mrp: parseFloat(item.mrp) || 0,
          value: parseFloat(item.value) || 0,
          valueWithTax: parseFloat(item.valueWithTax) || 0,
        })),
        summary: {
          ...summary,
          grossAmount: parseFloat(summary.grossAmount) || 0,
          discount: parseFloat(summary.discount) || 0,
          cgstTax: parseFloat(summary.cgstTax) || 0,
          sgstTax: parseFloat(summary.sgstTax) || 0,
          igstTax: parseFloat(summary.igstTax) || 0,
          cessTax: parseFloat(summary.cessTax) || 0,
          add: parseFloat(summary.add) || 0,
          less: parseFloat(summary.less) || 0,
          cashDiscount: parseFloat(summary.cashDiscount) || 0,
          roundedOff: parseFloat(summary.roundedOff) || 0,
          netAmount: parseFloat(summary.netAmount) || 0,
        },
        createdAt: billToEdit ? billToEdit.createdAt : serverTimestamp(),
        createdBy: currentUser.email,
        updatedAt: serverTimestamp(),
      };
      if (billToEdit && billToEdit.id) {
        const returnRef = ref(database, `users/${encodedEmail}/purchaseReturns/${billToEdit.id}`);
        await set(returnRef, returnData);
        alert('Purchase return updated successfully!');
      } else {
        const newReturnRef = push(returnsRef);
        await set(newReturnRef, returnData);
        alert('Purchase return saved successfully!');
      }
      onClose();
    } catch (error) {
      console.error('Error saving purchase return:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      alert(`Failed to save purchase return: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '1rem',
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      border: '1px solid #e2e8f0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      margin: '0 auto',
      maxWidth: '1400px',
      overflowX: 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2563eb',
    },
    headerButtons: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    backButton: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      backgroundColor: '#6b7280',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
      transform: 'translateY(0)',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1.5rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
    },
    section1: {
      backgroundColor: '#fff',
      padding: '1.25rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
      paddingBottom: '100px',
    },
    sectionHeading: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '0.75rem',
    },
    label: {
      display: 'block',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    input: {
      display: 'block',
      width: '80%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    tableContainer: {
      marginTop: '1rem',
    
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      position: 'relative',
      maxWidth: '100%',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(241,245,249,0.95) 100%)',
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.75rem',
      minWidth: '1200px',
    },
    tableHeader: {
      padding: '0.5rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
      whiteSpace: 'nowrap',
    },
    tableCell: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
      position: 'relative',
    },
    tableInput: {
      border: '1px solid #d1d5db',
      padding: '0.3rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.75rem',
      boxSizing: 'border-box',
      width: '80px',
      height: '28px',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    newLineButton: {
      marginTop: '1rem',
      padding: '0.75rem 1.5rem',
      borderRadius: '6px',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.85rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(37, 99, 235, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.3rem 0.6rem',
      borderRadius: '4px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.7rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    summaryContainer: {
      marginTop: '1.5rem',
      width: '100%',
      maxWidth: '250px',
      marginLeft: 'auto',
    },
    summaryBox: {
      border: '1px solid #e2e8f0',
      padding: '1rem',
      borderRadius: '8px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.5rem',
      fontSize: '0.8rem',
      color: '#374151',
    },
    summaryInput: {
      border: '1px solid #d1d5db',
      padding: '0.4rem',
      width: '40%',
      borderRadius: '4px',
      fontSize: '0.8rem',
      textAlign: 'right',
      backgroundColor: '#f3f4f6',
    },
    summaryTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      fontWeight: '600',
      borderTop: '1px solid #e2e8f0',
      paddingTop: '0.5rem',
      marginTop: '0.75rem',
      color: '#1f2937',
    },
    actionButtons: {
      marginTop: '1.5rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e2e8f0',
      flexWrap: 'wrap',
    },
    saveButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    cancelButton: {
      padding: '0.5rem 1.25rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      fontSize: '0.8rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 4px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    suggestions: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '600px',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      zIndex: 1000,
      maxHeight: '300px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      overflowY: 'auto',
    },
    suggestionTable: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    suggestionTh: {
      padding: '0.5rem',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.7rem',
      textAlign: 'center',
      border: '1px solid #1e40af',
    },
    suggestionTd: {
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      fontSize: '0.75rem',
      textAlign: 'center',
    },
    suggestionItem: {
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    suggestionActive: {
      backgroundColor: '#e0f2fe',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#718096', fontSize: '1rem' }}>
          <h2>Please login to access the Purchase Return</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            {billToEdit ? 'Edit Purchase Return' : 'Purchase Return'}
          </div>
          <div style={styles.headerButtons}>
            <button
              onClick={onClose}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
            >
              ← Back
            </button>
          </div>
        </div>
        <div style={styles.formLayout}>
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📋 Supplier Details</h2>
            <div style={styles.gridContainer}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  style={styles.select}
                  disabled
                >
                  <option value="Purchase Return">Purchase Return</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Supplier</label>
                <select
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleFormChange}
                  style={styles.select}
                >
                  <option value="">Select</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  style={styles.input}
                  readOnly
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleFormChange}
                  style={styles.input}
                  readOnly
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pin Code</label>
                <input
                  type="text"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleFormChange}
                  style={styles.input}
                  readOnly
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Return No</label>
                <input
                  type="text"
                  name="returnNo"
                  value={formData.returnNo}
                  onChange={handleFormChange}
                  style={styles.input}
                  readOnly
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Return Date</label>
                <input
                  type="date"
                  name="returnDate"
                  value={formData.returnDate}
                  onChange={handleFormChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Invoice No</label>
                <select
                  name="invoiceNo"
                  value={formData.invoiceNo}
                  onChange={handleFormChange}
                  style={styles.select}
                >
                  <option value="">Select</option>
                  {invoices
                    .filter((inv) => inv.supplier === formData.supplier)
                    .map((invoice) => (
                      <option key={invoice.id} value={invoice.invoiceNo}>
                        {invoice.invoiceNo}
                      </option>
                    ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Invoice Date</label>
                <input
                  type="text"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleFormChange}
                  style={styles.input}
                  readOnly
                />
              </div>
            </div>
          </div>
          <div style={styles.section1}>
            <h2 style={styles.sectionHeading}>📦 Items</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, width: '60px' }}>S.No</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>BAR CODE</th>
                    <th style={{ ...styles.tableHeader, width: '220px' }}>ITEM DESCRIPTION</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>UOM</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>BATCH</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>EXPIRY DATE</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>QTY</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>AVAILABLE QTY</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>RETURN QTY</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>DISC%</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>CGST%</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>SGST%</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>CESS AMT</th>
                    <th style={{ ...styles.tableHeader, width: '90px' }}>MRP</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>REMARKS</th>
                    <th style={{ ...styles.tableHeader, width: '100px' }}>PURCHASE RATE</th>
                    <th style={{ ...styles.tableHeader, width: '120px' }}>PURCHASE WITH TAX</th>
                    <th style={{ ...styles.tableHeader, width: '80px' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td style={{ ...styles.tableCell, fontWeight: '600', color: '#2563eb', width: '60px' }}>
                        {item.id}
                      </td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="text"
                          value={item.barCode}
                          onChange={(e) => handleItemChange(index, 'barCode', e.target.value)}
                          style={styles.tableInput}
                          readOnly
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '220px', position: 'relative' }}>
                        <input
                          type="text"
                          value={item.itemDescription}
                          onChange={(e) => handleItemChange(index, 'itemDescription', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          style={{ ...styles.tableInput, width: '200px' }}
                          placeholder="Search item..."
                          name="itemDescription"
                        />
                        {activeRowId === index && filteredItems.length > 0 && (
                          <div style={styles.suggestions} className="suggestions-table">
                            <table style={styles.suggestionTable}>
                              <thead>
                                <tr>
                                  <th style={{ ...styles.suggestionTh, width: '120px' }}>Bar Code</th>
                                  <th style={{ ...styles.suggestionTh, width: '220px' }}>Product</th>
                                  <th style={{ ...styles.suggestionTh, width: '90px' }}>MRP</th>
                                  <th style={{ ...styles.suggestionTh, width: '80px' }}>Qty</th>
                                  <th style={{ ...styles.suggestionTh, width: '100px' }}>Rate</th>
                                  <th style={{ ...styles.suggestionTh, width: '100px' }}>Unit</th>
                                  <th style={{ ...styles.suggestionTh, width: '100px' }}>Batch</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredItems.map((product, idx) => (
                                  <tr
                                    key={idx}
                                    style={{
                                      ...styles.suggestionItem,
                                      ...(activeSuggestionIndex === idx ? styles.suggestionActive : {}),
                                    }}
                                    onClick={() => handleItemSelect(index, product)}
                                    onMouseEnter={() => setActiveSuggestionIndex(idx)}
                                  >
                                    <td style={styles.suggestionTd}>{product.barCode || 'N/A'}</td>
                                    <td style={styles.suggestionTd}>{product.item || 'Unknown Product'}</td>
                                    <td style={styles.suggestionTd}>₹{parseFloat(product.mrp || 0).toFixed(2)}</td>
                                    <td style={styles.suggestionTd}>{product.qty || '0'}</td>
                                    <td style={styles.suggestionTd}>₹{parseFloat(product.pRate || 0).toFixed(2)}</td>
                                    <td style={styles.suggestionTd}>{product.unit || 'N/A'}</td>
                                    <td style={styles.suggestionTd}>{product.batch || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>
                        <input
                          type="text"
                          value={item.uom}
                          onChange={(e) => handleItemChange(index, 'uom', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>
                        <input
                          type="text"
                          value={item.batch}
                          onChange={(e) => handleItemChange(index, 'batch', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="text"
                          value={item.expiryDate}
                          onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <input
                          type="text"
                          value={item.qty}
                          onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>
                        <input
                          type="text"
                          value={item.avgAvailableQty}
                          onChange={(e) => handleItemChange(index, 'avgAvailableQty', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>
                        <input
                          type="text"
                          value={item.returnQty}
                          onChange={(e) => handleItemChange(index, 'returnQty', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <input
                          type="text"
                          value={item.disc}
                          onChange={(e) => handleItemChange(index, 'disc', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <input
                          type="text"
                          value={item.camt}
                          onChange={(e) => handleItemChange(index, 'camt', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <input
                          type="text"
                          value={item.samt}
                          onChange={(e) => handleItemChange(index, 'samt', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <input
                          type="text"
                          value={item.cessAmt}
                          onChange={(e) => handleItemChange(index, 'cessAmt', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '90px' }}>
                        <input
                          type="text"
                          value={item.mrp}
                          onChange={(e) => handleItemChange(index, 'mrp', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '100px' }}>
                        <input
                          type="text"
                          value={item.value}
                          onChange={(e) => handleItemChange(index, 'value', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '120px' }}>
                        <input
                          type="text"
                          value={item.valueWithTax}
                          onChange={(e) => handleItemChange(index, 'valueWithTax', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={{ ...styles.tableCell, width: '80px' }}>
                        <button
                          onClick={() => handleDeleteItem(index, item.barCode)}
                          style={styles.deleteButton}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 1px 4px rgba(220, 38, 38, 0.3)';
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleAddItem}
              style={styles.newLineButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 10px rgba(37, 99, 235, 0.3)';
              }}
            >
              + Add Item
            </button>
          </div>
          <div style={styles.section}>
            <h2 style={styles.sectionHeading}>📊 Summary</h2>
            <div style={styles.summaryContainer}>
              <div style={styles.summaryBox}>
                <div style={styles.summaryRow}>
                  <span>Gross Amount</span>
                  <input
                    type="text"
                    name="grossAmount"
                    value={Number(summary.grossAmount).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Discount</span>
                  <input
                    type="text"
                    name="discount"
                    value={Number(summary.discount).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>CGST Amount</span>
                  <input
                    type="text"
                    name="cgstTax"
                    value={Number(summary.cgstTax).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>SGST Amount</span>
                  <input
                    type="text"
                    name="sgstTax"
                    value={Number(summary.sgstTax).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>IGST Tax</span>
                  <input
                    type="text"
                    name="igstTax"
                    value={Number(summary.igstTax).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>CESS</span>
                  <input
                    type="text"
                    name="cessTax"
                    value={Number(summary.cessTax).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Add</span>
                  <input
                    type="text"
                    name="add"
                    value={Number(summary.add).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Less</span>
                  <input
                    type="text"
                    name="less"
                    value={Number(summary.less).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Cash Discount</span>
                  <input
                    type="text"
                    name="cashDiscount"
                    value={Number(summary.cashDiscount).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Rounded Off</span>
                  <input
                    type="text"
                    name="roundedOff"
                    value={Number(summary.roundedOff).toFixed(2)}
                    onChange={handleSummaryChange}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryTotal}>
                  <span>Net Amount</span>
                  <span>₹{Number(summary.netAmount).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.actionButtons}>
            <button
              onClick={saveReturn}
              style={styles.saveButton}
              disabled={isSaving}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSaving) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 1px 4px rgba(16, 185, 129, 0.3)';
                }
              }}
            >
              {isSaving ? 'Saving...' : (billToEdit ? '✅ Update' : '✅ Save')}
            </button>
            <button
              onClick={onClose}
              style={styles.cancelButton}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 1px 4px rgba(220, 38, 38, 0.3)';
              }}
            >
              ❌ Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseReturn;